// World connectivity for the 2008 map set. Hand-authored: fieldmapdata.xml lists
// scenery but no warp coordinates, so we place each door portal over the spot
// where the map art already draws a doorway / road exit. Two districts:
//   Domino City, map_d01_01 (Station Square hub) ↔ d02 ↔ d03 ↔ d04_01, + d05 (stadium)
//   Duel Academy, map_a01 (entrance) ↔ a02 (courtyard) ↔ a03 (arena) / a04 (dorm)
// The personal room (map_r01) opens onto the Domino hub; the hub warps to Academy.
//
// Portals are invisible triggers, there is NO door icon. The maps already draw
// the doors/roads, so the player just walks into them. Exits sit on opposite
// edges of connected maps (exit-right ↔ enter-left) so that arrival next to the
// matching door (see WorldRenderer.arrivalSpawn) reads as walking straight
// through.

import type { Facing } from "@ygoevo/world-protocol";
import { sceneMap } from "./sceneMaps.ts";

export interface SpawnPoint {
  x: number;
  y: number;
  facing: Facing;
}

export interface Portal {
  x: number;
  y: number;
  /** Destination map id. */
  to: string;
  /** Area name for the destination (documentation / future signposts). */
  label: string;
  /** Trigger radius in world px (default 64). */
  radius?: number;
  /** Exact trigger rectangle [x0,y0,x1,y1] world px. Scene maps author this
   *  directly; legacy maps leave it undefined and bind to an atari zone instead. */
  rect?: [number, number, number, number];
}

/** Default spawn (map centre-ish) when entering without a specific door arrival. */
export const SPAWNS: Record<string, SpawnPoint> = {
  map_r01: { x: 640, y: 450, facing: "s" },
  map_d01_01: { x: 1280, y: 950, facing: "s" },
  map_d02: { x: 1024, y: 820, facing: "s" },
  map_d03: { x: 1024, y: 950, facing: "s" },
  map_d04_01: { x: 1024, y: 820, facing: "s" },
  map_d05: { x: 512, y: 760, facing: "n" },
  map_a01: { x: 1024, y: 820, facing: "s" },
  map_a02: { x: 1024, y: 820, facing: "s" },
  map_a03: { x: 1024, y: 820, facing: "s" },
  map_a04: { x: 1024, y: 1180, facing: "n" },
};

const R = 70;

export const MAP_PORTALS: Record<string, Portal[]> = {
  // Personal room → out to Domino City. Sits on the floor in front of the blue
  // arched door drawn in the top-right wall (not the room centre).
  map_r01: [
    { x: 660, y: 300, to: "map_d01_01", label: "Domino City", radius: 90 },
  ],
  // Domino City hub (Station Square, 2560×1792). Four roads radiate from the
  // plaza, one portal at each road's edge.
  map_d01_01: [
    { x: 1280, y: 1720, to: "map_r01", label: "My Room", radius: R },
    { x: 2470, y: 900, to: "map_d02", label: "Card Shop St.", radius: R },
    { x: 90, y: 900, to: "map_d05", label: "KaibaCorp Stadium", radius: R },
    { x: 1280, y: 80, to: "map_a01", label: "Duel Academy", radius: R },
  ],
  // Domino, Card Shop Street (2048×1536). The "GAME" storefront in the top-left is
  // the Kame Game shop (clickable, see interactables). The hub return is the big door
  // zone the atari paints along the bottom edge (hint coord sits on it so the renderer
  // snaps the trigger there); the next link has no painted zone here, so it keeps the
  // hand-placed circle.
  map_d02: [
    { x: 1220, y: 1420, to: "map_d01_01", label: "Station Square", radius: R },
    { x: 1960, y: 780, to: "map_d03", label: "Arrival Ship", radius: R },
  ],
  // Duelist Kingdom, Arrival Ship (2048×1792). The harbour/dock the duelists
  // sail in on for the first YGO season: the whole left edge is the moored ship's
  // hull, so the return door sits on the open dock pavement just clear of it (was
  // on the ship at the far-left edge). Reached on foot from Card Shop St.; the
  // map disk lists it under its own "Duelist Kingdom Island" section.
  map_d03: [
    { x: 234, y: 1044, to: "map_d02", label: "Card Shop St.", radius: R },
    { x: 1960, y: 900, to: "map_d04_01", label: "KaibaCorp Entrance", radius: R },
    // Top of the gangway (the deck landing is walkable): board the ship → sail to
    // the island. The DK areas themselves are V2 scene maps (./maps/*.json).
    { x: 900, y: 320, to: "map_dk_beach", label: "Landing Beach", radius: R },
  ],
  // Domino, Promenade (2048×1536; dragon statues + central walkway). Return door on
  // the open pavement at the lower-left (was sitting on a dragon statue at the edge).
  map_d04_01: [
    { x: 130, y: 1000, to: "map_d03", label: "Arrival Ship", radius: R },
  ],
  // KaibaCorp Stadium (1024×1024, 4×4 grid of 256px tiles).
  map_d05: [
    { x: 512, y: 980, to: "map_d01_01", label: "Station Square", radius: R },
  ],
  // Duel Academy, Entrance grounds (2048×1536). Three atari door zones, wired
  // GX-lore-accurately: the stairs up into the main building → the hall, the
  // cobblestone path off the left → Domino City, and the path off the bottom →
  // the Slifer dorm (the dorms are separate buildings on the grounds, not inside
  // the hall). Hint coords sit on the real zones so the renderer snaps each one.
  map_a01: [
    { x: 1799, y: 134, to: "map_a02", label: "Main Hall", radius: R },
    { x: 100, y: 1016, to: "map_d01_01", label: "Domino City", radius: R },
    { x: 665, y: 1462, to: "map_a04", label: "Dorm", radius: R },
  ],
  // Duel Academy, Main Hall / "Courtyard" (2048×1536). The hall has THREE atari
  // zones but only two are warps: the entrance ramp (bottom-left → back outside)
  // and the staircase (right → up to the Arena). The third, the building that
  // literally reads "CARD SHOP" in the centre, is Dorothy's shop and is handled
  // as a click-to-open shop fixture (see interactables.ts), NOT a warp, so it no
  // longer mis-routes to the Arena.
  map_a02: [
    { x: 196, y: 1400, to: "map_a01", label: "Entrance", radius: R },
    { x: 1980, y: 884, to: "map_a03", label: "Arena", radius: R },
  ],
  // Duel Academy, Arena (the duel-field hall; 2048×1536). One door, back to the
  // main hall (atari zone at the bottom-right).
  map_a03: [
    { x: 1718, y: 1442, to: "map_a02", label: "Main Hall", radius: R },
  ],
  // Duel Academy, Slifer dorm (2048×1536). One door, back out to the entrance
  // grounds (atari zone at the bottom).
  map_a04: [
    { x: 1622, y: 1429, to: "map_a01", label: "Entrance", radius: R },
  ],

  // NOTE: the full-size `map_d01` (Central Plaza) and `map_d04` (Promenade) are
  // intentionally NOT wired. They ship base terrain but no object-placement data
  // exists for them in any manifest, so they render as bare/broken plazas, they
  // were dropped from the map disk rather than shown incomplete.
};

export function portalsFor(mapId: string): Portal[] {
  const s = sceneMap(mapId);
  if (s) return s.portals.map((p) => ({ x: p.x, y: p.y, to: p.to, label: p.label, radius: p.radius, rect: p.rect }));
  return MAP_PORTALS[mapId] ?? [];
}

export function spawnFor(mapId: string): SpawnPoint {
  const s = sceneMap(mapId);
  if (s) return s.spawn;
  return SPAWNS[mapId] ?? { x: 512, y: 512, facing: "s" };
}

/** HUD area name, scene-map aware. */
export function titleFor(mapId: string): string {
  return sceneMap(mapId)?.title ?? MAP_TITLES[mapId] ?? mapId;
}

/** Human-readable area names for the HUD (2008 world). */
export const MAP_TITLES: Record<string, string> = {
  map_r01: "MY ROOM",
  map_d01_01: "DOMINO CITY, STATION SQUARE",
  map_d02: "DOMINO CITY, CARD SHOP ST.",
  map_d03: "DUELIST KINGDOM, ARRIVAL SHIP",
  map_d04_01: "DOMINO CITY, KAIBACORP ENTRANCE",
  map_d05: "KAIBACORP STADIUM",
  map_a01: "DUEL ACADEMY, ENTRANCE",
  map_a02: "DUEL ACADEMY, MAIN HALL",
  map_a03: "DUEL ACADEMY, ARENA",
  map_a04: "DUEL ACADEMY, SLIFER DORM",
};

// ── Map disk (fast-travel) directory ────────────────────────────────────────
// The "map disk" lets a player teleport to any known area. Entries are grouped
// by district and use short labels; the disk art comes from maps/map_switch.
export interface MapDiskEntry {
  id: string;
  label: string;
}
export interface MapDiskDistrict {
  name: string;
  /** Asset path to the district disk icon. */
  disk: string;
  maps: MapDiskEntry[];
}

export const MAP_DIRECTORY: MapDiskDistrict[] = [
  {
    name: "Domino City",
    disk: "maps/map_switch/map_disk_d01_01.png",
    maps: [
      { id: "map_d01_01", label: "Station Square" },
      { id: "map_d02", label: "Card Shop St." },
      { id: "map_d04_01", label: "KaibaCorp Entrance" },
      { id: "map_d05", label: "KaibaCorp Stadium" },
      { id: "map_r01", label: "My Room" },
    ],
  },
  {
    name: "Duel Academy",
    disk: "maps/map_switch/map_disk_a01_01.png",
    maps: [
      { id: "map_a01", label: "Entrance" },
      { id: "map_a02", label: "Main Hall" },
      { id: "map_a03", label: "Arena" },
      { id: "map_a04", label: "Slifer Dorm" },
    ],
  },
  {
    // Season-1 Duelist Kingdom arc. For now this is just the harbour the duelists
    // sail in on (map_d03, the moored cruise ship). Reused the Domino disk icon;
    // there is no DK-specific disk art in maps/map_switch yet.
    name: "Duelist Kingdom Island",
    disk: "maps/map_switch/map_disk_d01_01.png",
    maps: [
      { id: "map_d03", label: "Arrival Ship" },
      { id: "map_dk_beach", label: "Landing Beach" },
      { id: "map_dk_forest", label: "Forest" },
      { id: "map_dk_grassland", label: "Grassland" },
      { id: "map_dk_coast", label: "Rocky Coast" },
      { id: "map_dk_caves", label: "Caves" },
      { id: "map_dk_graveyard", label: "Graveyard" },
      { id: "map_dk_labyrinth", label: "Labyrinth" },
      { id: "map_dk_approach", label: "Castle Approach" },
      { id: "map_dk_castle_gate", label: "Castle Gate" },
      { id: "map_dk_castle_hall", label: "Great Hall" },
      { id: "map_dk_rooftop", label: "Rooftop" },
    ],
  },
];
